import {
  FormGroup,
  HTMLSelect,
  InputGroup,
  Intent,
  TextArea,
} from "@blueprintjs/core";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useMe } from "../../hooks/useMe";
import { usePhonebook } from "../../hooks/usePhonebook";
import { ROLES, useRoles } from "../../hooks/useRoles";
import type { Contact } from "../../types/phonebook";
import { sortAlphabetically } from "../../utils/sorting";
import {
  PhonebookFormSection,
  PhonebookTokenList,
} from "./PhonebookFormSection";

interface PhonebookFormProps {
  content: Contact;
  onUpdate: (data: Contact) => void;
  onValidityChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

export const PhonebookContactForm = ({
  content,
  onUpdate,
  onValidityChange,
  readOnly = true,
}: PhonebookFormProps) => {
  const me = useMe();
  const { roles } = useRoles();
  const { organizations } = usePhonebook();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isAdmin = roles.includes(ROLES.ADMIN);

  const operatorOptions = useMemo(() => {
    const organizationNames = sortAlphabetically(
      organizations?.map((org) => org.name) ?? [],
    );

    if (isAdmin) {
      return organizationNames;
    }

    const allowedOrganizationNames = new Set(organizationNames);

    return (
      sortAlphabetically(
        me?.organizations?.filter((op) => allowedOrganizationNames.has(op)) ??
          [],
      )
    );
  }, [isAdmin, me?.organizations, organizations]);

  const validate = (data: Contact) => {
    const newErrors: Record<string, string> = {};

    if (!data.contactName || data.contactName.trim().length < 3) {
      newErrors.contactName = "Name must be at least 3 characters";
    }

    if (!data.phoneNumber || !isValidPhoneNumber(data.phoneNumber)) {
      newErrors.phoneNumber = "Invalid phone number";
    }

    if (!data.email || data.email.trim().length < 3) {
      newErrors.email = "Email must not be empty";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "Invalid email address";
    }

    return newErrors;
  };

  const updateFields = (patch: Partial<Contact>) => {
    const newContent = { ...content, ...patch };
    onUpdate(newContent);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    updateFields({ [name]: value });
  };

  const removeOrganization = (index: number) => {
    updateFields({
      organizations: (content.organizations ?? []).filter((_, i) => i !== index),
    });
  };

  useEffect(() => {
    const errs = validate(content);
    setErrors(errs);
    onValidityChange?.(Object.keys(errs).length === 0);
  }, [content]);

  return (
    <div className="focusoc-form-shell max-h-[72vh] w-[720px] max-w-[calc(100vw-4rem)] overflow-y-auto pr-1">
      <div className="flex flex-col gap-4">
        <PhonebookFormSection
          eyebrow={readOnly ? "Read only" : "Editable"}
          rightContent={
            content?.id ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
                {content.id}
              </span>
            ) : undefined
          }
          title="Contact"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <FormGroup
              helperText={errors.contactName}
              intent={errors.contactName ? Intent.DANGER : Intent.NONE}
              label="Contact name"
              labelFor="contact-name-input"
            >
              <InputGroup
                id="contact-name-input"
                intent={errors.contactName ? Intent.DANGER : Intent.NONE}
                name="contactName"
                onChange={handleChange}
                placeholder="Contact name"
                readOnly={readOnly}
                value={content?.contactName || ""}
              />
            </FormGroup>

            <FormGroup label="Contact role" labelFor="role-input">
              <InputGroup
                id="role-input"
                name="role"
                onChange={handleChange}
                placeholder="Contact role"
                readOnly={readOnly}
                value={content?.role || ""}
              />
            </FormGroup>

            <FormGroup
              helperText={errors.phoneNumber}
              intent={errors.phoneNumber ? Intent.DANGER : Intent.NONE}
              label="Phone number"
              labelFor="phone-number-input"
            >
              <InputGroup
                id="phone-number-input"
                intent={errors.phoneNumber ? Intent.DANGER : Intent.NONE}
                name="phoneNumber"
                onChange={handleChange}
                placeholder="Phone number"
                readOnly={readOnly}
                value={content?.phoneNumber || ""}
              />
            </FormGroup>

            <FormGroup
              helperText={errors.email}
              intent={errors.email ? Intent.DANGER : Intent.NONE}
              label="Email"
              labelFor="email-input"
            >
              <InputGroup
                id="email-input"
                intent={errors.email ? Intent.DANGER : Intent.NONE}
                name="email"
                onChange={handleChange}
                placeholder="Email"
                readOnly={readOnly}
                value={content?.email || ""}
              />
            </FormGroup>
          </div>
        </PhonebookFormSection>

        <PhonebookFormSection eyebrow="Organizations and notes" title="Access">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <FormGroup label="Organizations">
              {!readOnly && (
                <HTMLSelect
                  className="w-full"
                  fill
                  onChange={(e) => {
                    const selected = e.target.value;
                    if (
                      selected &&
                      !(content.organizations ?? []).includes(selected)
                    ) {
                      updateFields({
                        organizations: [
                          ...(content.organizations ?? []),
                          selected,
                        ],
                      });
                    }
                  }}
                  options={[
                    {
                      label: "- Select organization -",
                      value: "",
                      disabled: true,
                    },
                    ...operatorOptions.map((operator) => ({
                      label: operator,
                      value: operator,
                    })),
                  ]}
                  value=""
                />
              )}

              <PhonebookTokenList
                items={content.organizations ?? []}
                onRemove={removeOrganization}
                readOnly={readOnly}
              />
            </FormGroup>

            <FormGroup label="Notes" labelFor="notes-input">
              <TextArea
                className="min-h-[104px] resize-y !leading-relaxed"
                fill
                growVertically
                id="notes-input"
                name="notes"
                onChange={handleChange}
                placeholder="Notes"
                readOnly={readOnly}
                value={content?.notes || ""}
              />
            </FormGroup>
          </div>
        </PhonebookFormSection>
      </div>
    </div>
  );
};
